import { useQuery } from '@tanstack/react-query';
import {
  fetchMyCompanyArchive,
  fetchMyCompanyFindings,
  fetchNetworkArchive,
  fetchNetworkFindings,
} from '@/api/capa';
import type { QuickFilterMode } from '../-types';

interface UseFindingsQueryParams {
  mode: QuickFilterMode;
  isArchiveView: boolean;
  page: number;
  count: number;
  sort?: string;
  filters?: Record<string, string[]>;
}

function buildQs(params: UseFindingsQueryParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, params.page - 1)));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filters && Object.keys(params.filters).length > 0) {
    const flat: Record<string, string> = {};
    for (const [key, vals] of Object.entries(params.filters)) {
      if (vals.length === 0) continue;
      // tags supports comma-separated IDs; all others are single exact-match strings
      flat[key] = key === 'tags' ? vals.join(',') : vals[0]!;
    }
    if (Object.keys(flat).length > 0) qs.set('filter', JSON.stringify(flat));
  }
  return qs.toString();
}

export function findingsQueryOptions(params: UseFindingsQueryParams) {
  const qs = buildQs(params);
  return {
    queryKey: [
      'capa-findings',
      params.mode,
      params.isArchiveView ? 'archive' : 'active',
      params,
    ] as const,
    queryFn: () => {
      if (params.mode === 'internal') {
        return params.isArchiveView
          ? fetchMyCompanyArchive(qs)
          : fetchMyCompanyFindings(qs);
      }
      return params.isArchiveView
        ? fetchNetworkArchive(qs)
        : fetchNetworkFindings(qs);
    },
  };
}

export function useFindingsQuery(params: UseFindingsQueryParams) {
  return useQuery(findingsQueryOptions(params));
}
